import { useEffect, useState, useRef } from "react";
import useScript from "react-script-hook";

const State = {
  HIDDEN: "hidden",
  VISIBLE: "visible",
  OPENED: "opened",
};

const MAX_CHECK_INTERVAL = 10_000;

const useHelpkit = () => {
  const [state, setState] = useState(State.HIDDEN);
  const [isFrontChatInitialized, setIsFrontChatInitialized] = useState(false);
  const [shouldLoadScript, setShouldLoadScript] = useState(false);
  const [pendingState, setPendingState] = useState(null);
  const timeoutRef = useRef(null);

  useScript({
    src: shouldLoadScript ? "https://www.helpkit.so/widget/script.js" : null,
    onload: () => {
      console.log("[Front] loaded");
      let totalCheckInterval = 0;

      const checkIframeReady = () => {
        if (window.Helpkit?.config?.id !== "") {
          setIsFrontChatInitialized(true);
          console.log("[Front] iframe ready - initialized");
        } else {
          const INTERVAL = 10;

          // Poll until iframe is ready
          timeoutRef.current = setTimeout(checkIframeReady, INTERVAL);
          totalCheckInterval += INTERVAL;
          if (totalCheckInterval > MAX_CHECK_INTERVAL) {
            console.error("[Front] iframe not ready - timeout");
            setIsFrontChatInitialized(true);
          }
        }
      };

      checkIframeReady();
    },
    checkForExisting: true,
    "data-project-id": "write",
    id: "helpkit-widget",
  });

  const showFront = (open) => {
    setShouldLoadScript(true);

    const targetState = open ? State.OPENED : State.VISIBLE;

    if (isFrontChatInitialized) {
      // Script is already loaded, can set state immediately
      setState(targetState);
    } else {
      // Script not loaded yet, store the latest state request
      // If called multiple times, the most recent call takes precedence
      setPendingState(targetState);
    }
  };

  const hideFront = () => {
    setState(State.HIDDEN);
    setPendingState(null); // Clear any pending state when explicitly hiding
  };

  // Apply pending state once script is initialized
  useEffect(() => {
    if (isFrontChatInitialized && pendingState) {
      setState(pendingState);
      setPendingState(null);
    }
  }, [isFrontChatInitialized, pendingState]);

  useEffect(() => {
    if (isFrontChatInitialized) {
      if (state === State.OPENED) {
        window.Helpkit.setupLauncherButton();
        window.Helpkit.showLaunchButton();
        window.Helpkit.show();
        console.log("[Front] open");
      } else if (state === State.VISIBLE) {
        window.Helpkit.setupLauncherButton();
        window.Helpkit.showLaunchButton();
        console.log("[Front] visible");
      } else if (state === State.HIDDEN) {
        window.Helpkit.hideLaunchButton();
        window.Helpkit.hide();
        console.log("[Front] hide");
      }
    }
  }, [isFrontChatInitialized, state]);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [showFront, hideFront];
};

export default useHelpkit;
